import { Fragment, useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Button, Text, Tooltip, makeStyles, tokens } from '@coreai/fluentui-react';
import { ArrowLeft16Regular, MoreVertical16Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  name: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  kindBadge: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorBrandForeground1,
  },
  moreButton: {
    color: tokens.colorNeutralForeground2,
  },
  backButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':active': {
      transform: 'scale(0.96)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
  },
  metadataRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXXS,
  },
  metadataItem: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  metadataSeparator: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    userSelect: 'none' as const,
  },
});

export interface EntityOverviewHeaderProps {
  /** Primary heading text (e.g., project name or app name). */
  name: string;
  /** Badge label rendered next to the name ("Project", "App", etc.). */
  kindLabel: string;
  /**
   * Optional extra content rendered immediately after the kind badge in the
   * title row (e.g. a "Free" grant badge). Callers own the element so they can
   * supply the right `<Badge>` appearance/color and accessible text.
   */
  titleAdornment?: ReactNode;
  /**
   * Metadata items rendered inline with bullet separators. Each item is a
   * ReactNode (string, fragment, span, etc.); the header wraps each one in
   * a `<Text>` automatically, so callers should NOT pass `<Text>` to avoid
   * nested typography components.
   */
  metadata: ReactNode[];
  /** Optional handler for the kebab menu. Omit to hide the button entirely. */
  onMoreActions?: () => void;
  /** aria-label for the kebab button. */
  moreActionsLabel?: string;
  /** aria-label for the metadata row. */
  metadataAriaLabel?: string;
  /**
   * Fallback handler for the back button. The button renders when either a
   * valid `location.state.from` origin is present or `onBack` is provided;
   * `onBack` is only called when no valid origin is available.
   */
  onBack?: () => void;
  /**
   * Accessible name + tooltip for the back button. Defaults to "Back" but
   * callers SHOULD pass a destination-aware label (e.g., "Back to apps")
   * so screen reader users get context without relying on visual cues
   * — WCAG 2.4.4 Link Purpose.
   */
  backLabel?: string;
}

/**
 * Reusable overview header shown above list pages (Apps under a project,
 * Workflows under an app, etc.). Renders an h1 title with a kind badge,
 * an optional kebab button, and a metadata row with bullet separators.
 */
export const EntityOverviewHeader = ({
  name,
  kindLabel,
  titleAdornment,
  metadata,
  onMoreActions,
  moreActionsLabel = 'More actions',
  metadataAriaLabel,
  onBack,
  backLabel = 'Back',
}: EntityOverviewHeaderProps) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  // Origin is set by the linking page via `navigate(to, { state: { from } })`.
  // When present, the back button returns the user to the exact page they came
  // from (preserving query params); otherwise we defer to the page-supplied
  // fallback (which goes to the canonical parent).
  const originFrom =
    typeof (location.state as { from?: unknown } | null)?.from === 'string'
      ? ((location.state as { from: string }).from)
      : null;
  const validOriginFrom = originFrom?.startsWith('/') &&
    !originFrom.startsWith('//')
    ? originFrom
    : null;
  const showBack = !!(validOriginFrom || onBack);
  const handleBack = useCallback(() => {
    if (validOriginFrom) {
      navigate(validOriginFrom, { replace: true });
      return;
    }
    onBack?.();
  }, [navigate, validOriginFrom, onBack]);

  return (
    <header className={styles.root}>
      <div className={styles.titleRow}>
        {showBack && (
          <Tooltip content={backLabel} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowLeft16Regular />}
              className={styles.backButton}
              aria-label={backLabel}
              onClick={handleBack}
            />
          </Tooltip>
        )}
        <Text as="h1" className={styles.name}>
          {name || '\u00A0'}
        </Text>
        <Badge
          appearance="tint"
          color="brand"
          size="small"
          shape="rounded"
          className={styles.kindBadge}
        >
          {kindLabel}
        </Badge>
        {titleAdornment}
        {onMoreActions && (
          <Tooltip content={moreActionsLabel} relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<MoreVertical16Regular />}
              className={styles.moreButton}
              aria-label={moreActionsLabel}
              onClick={onMoreActions}
            />
          </Tooltip>
        )}
      </div>
      <div className={styles.metadataRow} aria-label={metadataAriaLabel ?? `${kindLabel} metadata`}>
        {metadata.map((item, index) => (
          <Fragment key={index}>
            {index > 0 && <span className={styles.metadataSeparator} aria-hidden="true">•</span>}
            <Text size={200} className={styles.metadataItem}>{item}</Text>
          </Fragment>
        ))}
      </div>
    </header>
  );
};
